import React, { useMemo, useState } from 'react';

import useLabOrderStore from '../../stores/labOrderStore';
import { updateLabReviewDate } from '../../services/api/labApi';
import { plusDaysISO, todayISO } from '../common/ValidityControl';
import LabReportActions from '../lab/LabReportActions';
import {
  buildLabSummary,
  LAB_PILLS,
  LAB_PILL_META,
  labStatusColor,
} from './labSummaryProjection';

/**
 * LabSummary — the doctor's per-visit lab summary, parity with ProcedureSummary.
 * Status-filter pills (All / Planned / In progress / Completed / Cancelled) with
 * per-bucket counts, rows showing attribution / valid-till / Expired / cost, and
 * per-state actions: Extend + Cancel on Planned, view/download report on Completed.
 *
 * Single-sources its rows/counts from `buildLabSummary` so the inline Tests chips
 * and this panel never drift.
 */
function prettyDate(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10));
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Completed timestamp with time — "when the test was done".
function prettyDateTime(iso) {
  if (!iso) return '';
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return prettyDate(iso);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function LabRow({ row, isDark, visitId, patientId, onChanged }) {
  const { cancel, markDoneOutside, fetchForVisit, fetchRecentForPatient } = useLabOrderStore();
  const [busy, setBusy] = useState(false);
  const [editDate, setEditDate] = useState(null); // ISO string while editing
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [doneOpen, setDoneOpen] = useState(false);
  const [doneNote, setDoneNote] = useState('');
  const [err, setErr] = useState(null);
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  const exp = row.expiresAt ? String(row.expiresAt).slice(0, 10) : null;
  const isExpired = exp && row.bucket === 'planned' && exp < todayISO();
  // Manage actions are status-driven, not visit-scoped (full actions everywhere):
  // any still-advised, unassigned, non-standing order can be extended/cancelled.
  // Completed → report. Once assigned/in-progress it's locked.
  const canManage = row.status === 'advised' && !row.assigned && !row.standing;
  // "Mark done (outside)" — the patient got it done elsewhere. Available on any
  // not-yet-started (planned) order, in-house OR external; keeps the record as
  // done (vs cancel = won't be done). Standing parents are excluded.
  const canDoneOutside = row.bucket === 'planned' && !row.standing;

  const saveExpiry = async () => {
    if (!editDate) { setEditDate(null); return; }
    setBusy(true); setErr(null);
    const r = await updateLabReviewDate(row.id, { expires_at: editDate });
    setBusy(false);
    if (r?.success === false) { setErr(r.error || 'Could not extend the validity.'); return; }
    setEditDate(null);
    onChanged?.();
  };
  // Procedure-style inline cancel (no browser confirm): a reason field + Confirm/Back.
  const handleCancel = async () => {
    setBusy(true); setErr(null);
    const r = await cancel(row.id, { reason: cancelReason.trim() || 'Cancelled from prescription' });
    setBusy(false);
    if (r?.success === false) { setErr(r.error || 'Could not cancel the lab order.'); return; }
    setCancelOpen(false); setCancelReason('');
    // Sync both surfaces: the patient Lab summary AND the inline Tests row — but
    // only refetch THIS visit's order slice when the cancelled order belongs to the
    // open consult (a from-history row lives on a different visit).
    if (patientId) fetchRecentForPatient(patientId);
    if (visitId && row.order?.visit_id === visitId) fetchForVisit(visitId, { includeAdvised: true });
    onChanged?.();
  };
  // Mark done (outside): comment optional — the done-outside state is the record.
  const handleDoneOutside = async () => {
    setBusy(true); setErr(null);
    // Refresh both surfaces: the inline Tests row (byVisit — the order's own visit)
    // and the patient-scoped summary (byPatient).
    const vid = visitId || row.order?.visit_id || row.order?.origin_visit_id || null;
    const r = await markDoneOutside(row.id, { note: doneNote.trim() || null, visitId: vid, patientId });
    setBusy(false);
    if (r?.success === false) { setErr(r.error || 'Could not mark it done.'); return; }
    setDoneOpen(false); setDoneNote('');
    onChanged?.();
  };

  const miniBtn =
    'rounded-md px-2 py-1 text-xs font-semibold border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60';
  const fieldCls =
    'rounded-md px-2 py-1 text-xs border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';

  return (
    <div className={[
      'rounded-md border px-2.5 py-2 text-sm',
      isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-300',
    ].join(' ')}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: labStatusColor(row.status) }} aria-hidden="true" />
        <span className={['font-medium', isDark ? 'text-slate-100' : 'text-slate-800'].join(' ')}>{row.name}</span>
        <span className={muted}>{' · '}{row.statusLabel}</span>
        {row.standing && <span className={muted}>{' · standing'}</span>}
        {!row.assigned && row.bucket === 'planned' && !row.standing && (
          <span className={muted}>{' · awaiting assignment'}</span>
        )}
        {row.assignee && <span className={muted}>{' · 🔬 '}{row.assignee}</span>}
        {row.fromHistory && <span className={muted}>{' · from a previous visit'}</span>}
        {row.originDoctor && <span className={muted}>{' · advised by '}{row.originDoctor}</span>}
        {row.bucket === 'cancelled' && row.cancelledBy && <span className={muted}>{' · cancelled by '}{row.cancelledBy}</span>}
        {row.bucket === 'planned' && exp && !isExpired && (
          <span className={muted}>{' · valid till '}{prettyDate(exp)}</span>
        )}
        {isExpired && (
          <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-700 border border-rose-300 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700">
            Expired
          </span>
        )}
        {row.finalCost != null && <span className={muted}>{' · ₹'}{Number(row.finalCost).toLocaleString('en-IN')}</span>}
      </div>

      {/* Dates — advised / done / cancelled (who-advised shown above). */}
      <div className={['mt-0.5 text-xs', muted].join(' ')}>
        {row.advisedAt && <span>Advised {prettyDate(row.advisedAt)}</span>}
        {row.completedAt && <span>{row.advisedAt ? ' · ' : ''}Done {prettyDateTime(row.completedAt)}</span>}
        {row.cancelledAt && <span>{(row.advisedAt || row.completedAt) ? ' · ' : ''}Cancelled {prettyDate(row.cancelledAt)}{row.cancelledBy ? ` by ${row.cancelledBy}` : ''}</span>}
      </div>

      {/* Actions */}
      {(canManage || canDoneOutside) && !cancelOpen && !doneOpen && editDate === null && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {canManage && (
            <button type="button" className={miniBtn} disabled={busy} onClick={() => setEditDate(row.expiresAt ? String(row.expiresAt).slice(0, 10) : plusDaysISO(30))}>📅 Extend</button>
          )}
          {canDoneOutside && (
            <button type="button" className={`${miniBtn} !text-emerald-700 dark:!text-emerald-400`} disabled={busy} onClick={() => { setErr(null); setDoneNote(''); setDoneOpen(true); }}>✓ Done (outside)</button>
          )}
          {canManage && (
            <button type="button" className={`${miniBtn} !text-rose-600 dark:!text-rose-400`} disabled={busy} onClick={() => { setErr(null); setCancelReason(''); setCancelOpen(true); }}>✕ Cancel</button>
          )}
        </div>
      )}
      {/* Inline extend-expiry editor. */}
      {canManage && editDate !== null && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <input type="date" value={editDate} min={todayISO()} onChange={(e) => setEditDate(e.target.value)} className={fieldCls} />
          <button type="button" className={miniBtn} disabled={busy} onClick={saveExpiry}>Save</button>
          <button type="button" className={miniBtn} disabled={busy} onClick={() => setEditDate(null)}>✕</button>
        </div>
      )}
      {/* Inline "done outside" panel — optional comment → Confirm done / Back. */}
      {canDoneOutside && doneOpen && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <input
            type="text"
            value={doneNote}
            onChange={(e) => setDoneNote(e.target.value)}
            placeholder="Comment (optional) — e.g. done at City Lab"
            aria-label="Done-outside comment"
            className={`${fieldCls} flex-1 min-w-[8rem]`}
          />
          <button
            type="button"
            disabled={busy}
            onClick={handleDoneOutside}
            className="rounded-md px-2.5 py-1 text-xs font-semibold border bg-emerald-600 border-emerald-700 text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Confirm done'}
          </button>
          <button type="button" disabled={busy} className={miniBtn} onClick={() => { setDoneOpen(false); setDoneNote(''); }}>Back</button>
        </div>
      )}
      {/* Inline cancel panel — mirrors the Procedures row (reason → Confirm/Back, no browser confirm). */}
      {canManage && cancelOpen && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <input
            type="text"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Reason (optional)"
            aria-label="Cancellation reason"
            className={`${fieldCls} flex-1 min-w-[8rem]`}
          />
          <button
            type="button"
            disabled={busy}
            onClick={handleCancel}
            className="rounded-md px-2.5 py-1 text-xs font-semibold border bg-rose-600 border-rose-700 text-white hover:bg-rose-700 disabled:opacity-60"
          >
            {busy ? 'Cancelling…' : 'Confirm cancel'}
          </button>
          <button type="button" disabled={busy} className={miniBtn} onClick={() => { setCancelOpen(false); setCancelReason(''); }}>Back</button>
        </div>
      )}
      {err && <div className="mt-1 text-xs text-rose-600 dark:text-rose-400">{err}</div>}
      {/* Done-outside comment (if the doctor left one). */}
      {row.completedOutside && row.completionNote && (
        <div className={['mt-0.5 text-xs italic', muted].join(' ')}>“{row.completionNote}”{row.completedByName ? ` — ${row.completedByName}` : ''}</div>
      )}
      {row.bucket === 'completed' && (
        <div className="mt-1.5">
          {/* Doctor's review surface: report view/download ONLY — no payment-collect
              (that's reception's job). reportOnly hides the Collect/external controls. */}
          <LabReportActions order={row.order} onChanged={onChanged} reportOnly />
        </div>
      )}
    </div>
  );
}

export default function LabSummary({ orders = [], visitId, patientId, isDark = false, onChanged }) {
  const { rows, counts } = useMemo(() => buildLabSummary(orders), [orders]);
  const [filter, setFilter] = useState('all');

  if (!rows.length) {
    return (
      <div className={['text-sm italic', isDark ? 'text-slate-400' : 'text-slate-500'].join(' ')}>
        No lab tests on this visit yet.
      </div>
    );
  }

  const pills = ['all', ...LAB_PILLS];
  const visible = filter === 'all' ? rows : rows.filter((r) => r.bucket === filter);

  return (
    <div>
      <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
        {pills.map((id) => {
          const active = filter === id;
          const label = id === 'all' ? 'All' : LAB_PILL_META[id]?.label || id;
          const n = id === 'all' ? counts.all : (counts[id] ?? 0);
          const color = id === 'all' ? null : LAB_PILL_META[id]?.color;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              className={[
                'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border',
                active
                  ? 'bg-teal-600 text-white border-teal-700'
                  : (isDark
                    ? 'bg-slate-800 text-slate-200 border-slate-600 hover:bg-slate-700'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'),
              ].join(' ')}
            >
              {color && <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />}
              {label} · {n}
            </button>
          );
        })}
      </div>

      <div className="space-y-1.5">
        {visible.map((row) => (
          <LabRow key={row.id} row={row} isDark={isDark} visitId={visitId} patientId={patientId} onChanged={onChanged} />
        ))}
        {visible.length === 0 && (
          <div className={['text-xs italic', isDark ? 'text-slate-400' : 'text-slate-500'].join(' ')}>
            No {LAB_PILL_META[filter]?.label?.toLowerCase() || filter} tests.
          </div>
        )}
      </div>
    </div>
  );
}
